import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Tabs,
  Tooltip,
  Typography,
  Upload,
} from 'antd'
import type { UploadProps } from 'antd'
import {
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FileProtectOutlined,
  FolderOutlined,
  ProfileOutlined,
  RobotOutlined,
  RiseOutlined,
  SearchOutlined,
  SettingOutlined,
  SolutionOutlined,
  StarFilled,
  StarOutlined,
  SyncOutlined,
  TeamOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useToast } from '@/contexts/ToastContext'
import {
  canCreateAgentBuilderDashboardDraft,
  canCreateOrModifyAgentBuilderDashboards,
  canDeleteAgentBuilderDashboard,
} from '@/features/auth/permissions'
import { useAvailableReportPlatformTypes } from '@/hooks/useReports'
import { useAuthSession } from '@/lib/session'
import { useCanManageMcp } from '@/features/mcp/hooks'
import { permissions, UserTypes } from '@/types/authorization'
import { getAgentBuilderChatMessage, saveAgentBuilderDashboard } from './api'
import {
  invalidateAgentBuilderDashboardConsumers,
  useAgentBuilderAccess,
  useAgentBuilderCreateDashboardFromJsonMutation,
  useAgentBuilderCreateDashboardFromPromptMutation,
  useAgentBuilderClientUserEmailOptionsQuery,
  useAgentBuilderDashboardsQuery,
  useAgentBuilderDeleteDashboardMutation,
  useAgentBuilderSettingsQuery,
  useAgentBuilderUpdateDashboardMutation,
  useAgentBuilderUpdateDashboardFavoriteMutation,
  useAgentBuilderUpdateSettingsMutation,
} from './hooks'
import EditAgentBuilderDashboardModal from './components/EditAgentBuilderDashboardModal'
import AgentBuilderAccessGate from './components/AgentBuilderAccessGate'
import { parseDashboardDefinitionJson } from './coerceDashboardDefinition'
import type { AgentBuilderDashboard, DashboardDefinition } from './types'
import {
  XPM_DASHBOARD_SECTION_OPTIONS,
  getDashboardSectionOptions,
  isSecuredAgentBuilderPlatform,
  normalizeDashboardSection,
  sanitizeDashboardSection,
} from './sections'

const OTHER_SECTION_KEY = '__other__'

type DashboardSectionConfig = {
  key: string
  label: string
  icon: ReactNode
  accentClass: string
  iconClass: string
}

const SECTION_STYLES: Record<string, Omit<DashboardSectionConfig, 'key' | 'label'>> = {
  Billing: {
    icon: <BarChartOutlined />,
    accentClass: 'text-cyan-600',
    iconClass: 'bg-cyan-50 text-cyan-600',
  },
  Profitability: {
    icon: <RiseOutlined />,
    accentClass: 'text-green-600',
    iconClass: 'bg-green-50 text-green-600',
  },
  Job: {
    icon: <FolderOutlined />,
    accentClass: 'text-blue-600',
    iconClass: 'bg-blue-50 text-blue-600',
  },
  TeamMember: {
    icon: <TeamOutlined />,
    accentClass: 'text-orange-500',
    iconClass: 'bg-orange-50 text-orange-500',
  },
  WIP: {
    icon: <FileProtectOutlined />,
    accentClass: 'text-purple-700',
    iconClass: 'bg-purple-50 text-purple-700',
  },
  TaxLodgements: {
    icon: <AuditOutlined />,
    accentClass: 'text-red-500',
    iconClass: 'bg-red-50 text-red-500',
  },
  DataIntegrity: {
    icon: <CheckCircleOutlined />,
    accentClass: 'text-slate-500',
    iconClass: 'bg-slate-100 text-slate-500',
  },
  Xero: {
    icon: <SyncOutlined />,
    accentClass: 'text-teal-600',
    iconClass: 'bg-teal-50 text-teal-600',
  },
}

const FAVORITES_SECTION: DashboardSectionConfig = {
  key: 'favorites',
  label: 'Favorites',
  icon: <StarFilled />,
  accentClass: 'text-brand-600',
  iconClass: 'bg-brand-50 text-brand-600',
}

const OTHER_SECTION: DashboardSectionConfig = {
  key: OTHER_SECTION_KEY,
  label: 'Other',
  icon: <AppstoreOutlined />,
  accentClass: 'text-slate-500',
  iconClass: 'bg-slate-100 text-slate-500',
}

const DASHBOARD_SECTIONS: DashboardSectionConfig[] = XPM_DASHBOARD_SECTION_OPTIONS.map((option) => ({
  key: option.value,
  label: option.label,
  ...(SECTION_STYLES[option.value] ?? {
    icon: <AppstoreOutlined />,
    accentClass: 'text-slate-500',
    iconClass: 'bg-slate-100 text-slate-500',
  }),
}))

function getRootDashboardId(dashboard: AgentBuilderDashboard) {
  return dashboard.rootDashboardId ?? dashboard.rootId ?? dashboard.dashboardId
}

function extractDashboardTitle(dashboardJson: string) {
  const parsed = parseDashboardDefinitionJson(dashboardJson)
  return parsed?.title?.trim() || ''
}

function parseDashboardDefinition(dashboardJson: string): DashboardDefinition | null {
  return parseDashboardDefinitionJson(dashboardJson)
}

function getSectionConfig(section: string | null | undefined) {
  const normalizedSection = normalizeDashboardSection(section)
  return DASHBOARD_SECTIONS.find((item) => item.key === normalizedSection) ?? OTHER_SECTION
}

export default function AgentBuilderHomePage() {
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const access = useAgentBuilderAccess(clientId)

  if (!clientId || access.canAccess) {
    return <AgentBuilderHomeContent />
  }

  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd className="px-6 py-6 md:px-8">
      <div className="relative mx-auto w-full max-w-6xl">
        <div className="pt-1 pb-4">
          <Typography.Title level={3} className="!mb-1">Agent Builder</Typography.Title>
          <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
        </div>
        <AgentBuilderAccessGate clientId={clientId}>{null}</AgentBuilderAccessGate>
      </div>
    </FeatureThemeProvider>
  )
}

function AgentBuilderHomeContent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const session = useAuthSession()
  const { addToast } = useToast()
  const { hasAllPermissions, featureFlags } = useAuthorization()
  const clientId = session?.currentClientId ?? ''
  const canManageReportOptions = hasAllPermissions([permissions.viewNotification, permissions.manageReportOptions])
  const { canManage: canManageMcp } = useCanManageMcp(clientId)

  const dashboardsQuery = useAgentBuilderDashboardsQuery(clientId)
  const platformTypesQuery = useAvailableReportPlatformTypes(clientId)
  const createFromPrompt = useAgentBuilderCreateDashboardFromPromptMutation()
  const createFromJson = useAgentBuilderCreateDashboardFromJsonMutation(clientId)
  const updateDashboard = useAgentBuilderUpdateDashboardMutation(clientId)
  const updateFavorite = useAgentBuilderUpdateDashboardFavoriteMutation(clientId)
  const deleteDashboard = useAgentBuilderDeleteDashboardMutation(clientId)

  const isDiAdmin = canCreateAgentBuilderDashboardDraft(session)
  const canCreateDashboards = canCreateOrModifyAgentBuilderDashboards(session, featureFlags)
  const canEditDashboard = canCreateDashboards
    && (isDiAdmin || session?.currentUserTypeId === UserTypes.ClientAdmin)
  // ui/settings is [AdminOnly] on the API, so anyone else would only get a 403.
  const settingsQuery = useAgentBuilderSettingsQuery(clientId, isDiAdmin, isDiAdmin)
  const emailOptionsQuery = useAgentBuilderClientUserEmailOptionsQuery(clientId, isDiAdmin)
  const updateSettings = useAgentBuilderUpdateSettingsMutation(clientId)

  const [editingDashboard, setEditingDashboard] = useState<AgentBuilderDashboard | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('prompt')
  const [platformTypeId, setPlatformTypeId] = useState<number | null>(null)
  const [section, setSection] = useState<string | null>(null)
  const [prompt, setPrompt] = useState('')
  const [uploadedJson, setUploadedJson] = useState('')
  const [uploadedTitle, setUploadedTitle] = useState('')
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [saveInitialAsDraft, setSaveInitialAsDraft] = useState(false)
  const [uploadedPlatformTypeId, setUploadedPlatformTypeId] = useState<number | null>(null)
  const [isPromptPolling, setIsPromptPolling] = useState(false)
  const [modalError, setModalError] = useState('')
  const [search, setSearch] = useState('')

  const platformOptions = useMemo(() => {
    return (platformTypesQuery.data ?? [])
      .map((option) => ({ value: Number(option.value), label: option.name }))
      .filter((option) => Number.isFinite(option.value) && option.label)
  }, [platformTypesQuery.data])

  useEffect(() => {
    const firstPlatform = platformOptions.at(0)
    if (!firstPlatform) {
      setPlatformTypeId(null)
      return
    }

    if (!platformTypeId || !platformOptions.some((option) => option.value === platformTypeId)) {
      setPlatformTypeId(firstPlatform.value)
    }
  }, [platformOptions, platformTypeId])
  const filteredRows = useMemo(() => {
    const rows = dashboardsQuery.data ?? []
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) => (row.title || '').toLowerCase().includes(q))
  }, [dashboardsQuery.data, search])

  const favoriteRows = useMemo(() => filteredRows.filter((row) => row.isFavorite), [filteredRows])
  const impersonationEmailOptions = useMemo(
    () => (emailOptionsQuery.data ?? []).map((option) => ({
      value: option.email,
      label: option.fullName ? `${option.fullName} (${option.email})` : option.email,
    })),
    [emailOptionsQuery.data],
  )

  const groupedSections = useMemo(() => {
    const grouped = new Map<string, AgentBuilderDashboard[]>()
    for (const row of filteredRows) {
      const config = getSectionConfig(row.section)
      grouped.set(config.key, [...(grouped.get(config.key) ?? []), row])
    }

    const sections = DASHBOARD_SECTIONS
      .map((config) => ({ config, dashboards: grouped.get(config.key) ?? [] }))
      .filter((item) => item.dashboards.length > 0)

    const otherDashboards = grouped.get(OTHER_SECTION.key) ?? []
    if (otherDashboards.length > 0) {
      sections.push({ config: OTHER_SECTION, dashboards: otherDashboards })
    }

    return sections
  }, [filteredRows])

  function openDashboard(dashboard: AgentBuilderDashboard) {
    const rootDashboardId = getRootDashboardId(dashboard)
    if (!rootDashboardId) {
      addToast('This dashboard is missing a rootDashboardId.', 'error')
      return
    }

    navigate(`/reports/dashboard-builder/view?rootDashboardId=${encodeURIComponent(rootDashboardId)}`)
  }

  function toggleFavorite(dashboard: AgentBuilderDashboard) {
    const rootDashboardId = getRootDashboardId(dashboard)
    if (!rootDashboardId) {
      addToast('This dashboard is missing a rootDashboardId.', 'error')
      return
    }

    updateFavorite.mutate(
      {
        clientId,
        dashboardId: rootDashboardId,
        isFavorite: !dashboard.isFavorite,
      },
      {
        onError: (error) => {
          addToast(error instanceof Error ? error.message : 'Failed to update favorite.', 'error')
        },
      },
    )
  }

  function confirmDeleteDashboard(dashboard: AgentBuilderDashboard) {
    const rootDashboardId = getRootDashboardId(dashboard)
    if (!rootDashboardId) {
      addToast('This dashboard is missing a rootDashboardId.', 'error')
      return
    }
    const title = dashboard.title || 'Untitled Dashboard'
    Modal.confirm({
      title: `Delete "${title}"?`,
      content: 'This dashboard and all its versions will be deleted. This cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () =>
        deleteDashboard
          .mutateAsync({
            clientId,
            rootId: rootDashboardId,
            platformTypeId: dashboard.platformTypeId ?? null,
          })
          .then(() => {
            addToast('Dashboard deleted.', 'success')
          })
          .catch((error) => {
            addToast(error instanceof Error ? error.message : 'Failed to delete dashboard.', 'error')
            throw error
          }),
    })
  }

  async function updateImpersonationEmail(value: string | null | undefined) {
    try {
      await updateSettings.mutateAsync({
        clientId,
        impersonationEmail: value?.trim() || null,
      })
      addToast(value ? 'Agent Builder impersonation updated' : 'Agent Builder impersonation cleared', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to update Agent Builder impersonation.', 'error')
    }
  }

  function resetModal() {
    setActiveTab('prompt')
    setPrompt('')
    setUploadedJson('')
    setUploadedTitle('')
    setUploadedFileName('')
    setSaveInitialAsDraft(false)
    setSection(null)
    setUploadedPlatformTypeId(null)
    setIsPromptPolling(false)
    setModalError('')
    setPlatformTypeId(platformOptions[0]?.value ?? null)
  }

  function handlePlatformTypeChange(nextPlatformTypeId: number) {
    setPlatformTypeId(nextPlatformTypeId)
    // Each secured platform supports a different subset of sections, so drop a
    // selection the newly chosen platform cannot use.
    setSection((current) => (
      isSecuredAgentBuilderPlatform(nextPlatformTypeId)
        ? normalizeDashboardSection(current, nextPlatformTypeId)
        : null
    ))
  }

  function closeModal(force = false) {
    if (isCreating && !force) return
    setModalOpen(false)
    resetModal()
  }

  function showModalError(error: unknown, fallback: string) {
    const message = error instanceof Error && error.message ? error.message : fallback
    setModalError(message)
  }

  async function redirectToCreatedDashboard(dashboard: AgentBuilderDashboard) {
    const rootDashboardId = getRootDashboardId(dashboard)
    if (!rootDashboardId) {
      throw new Error('Created dashboard did not return a rootDashboardId.')
    }

    closeModal(true)
    navigate(`/reports/dashboard-builder/view?rootDashboardId=${encodeURIComponent(rootDashboardId)}`)
  }

  async function pollGeneratedDashboard(agentBuilderChatId: string) {
    for (let attempt = 0; attempt < 90; attempt += 1) {
      const status = await getAgentBuilderChatMessage(agentBuilderChatId)

      if (status.status === 'completed') {
        const rootDashboardId = status.result?.rootDashboardId ?? status.result?.dashboardId
        return {
          rootDashboardId,
          updatedDashboardJson: status.result?.updatedDashboardJson ?? null,
        }
      }

      if (status.status === 'failed') {
        throw new Error(status.errorMessage || 'Agent Builder dashboard generation failed.')
      }

      await new Promise((resolve) => setTimeout(resolve, 2000))
    }

    throw new Error('Agent Builder is still generating the dashboard. Please try again shortly.')
  }

  async function handleCreateFromPrompt() {
    setModalError('')
    if (!clientId) {
      setModalError('No active client selected.')
      return
    }
    if (!prompt.trim()) {
      setModalError('Enter a dashboard prompt first.')
      return
    }
    if (!platformTypeId) {
      setModalError('Select a platform before creating a dashboard.')
      return
    }
    const normalizedSection = isSecuredAgentBuilderPlatform(platformTypeId) ? normalizeDashboardSection(section, platformTypeId) : null
    if (isSecuredAgentBuilderPlatform(platformTypeId) && !normalizedSection) {
      setModalError('Select a dashboard section first.')
      return
    }

    try {
      const response = await createFromPrompt.mutateAsync({
        clientId,
        platformTypeId,
        section: normalizedSection,
        question: prompt.trim(),
        isDraft: isDiAdmin && saveInitialAsDraft,
      })
      if (!response.agentBuilderChatId) {
        throw new Error('Agent Builder did not return a generation id.')
      }

      setIsPromptPolling(true)
      const generated = await pollGeneratedDashboard(response.agentBuilderChatId)
      let rootDashboardId = generated.rootDashboardId

      if (!rootDashboardId && generated.updatedDashboardJson) {
        const parsedDashboard = parseDashboardDefinition(generated.updatedDashboardJson)
        const generatedDashboard = await saveAgentBuilderDashboard({
          clientId,
          platformTypeId: parsedDashboard?.platformTypeId || platformTypeId,
          section: normalizedSection,
          title: extractDashboardTitle(generated.updatedDashboardJson) || 'Agent Builder Dashboard',
          prompt: prompt.trim(),
          dashboardJson: generated.updatedDashboardJson,
          isDraft: isDiAdmin && saveInitialAsDraft,
        })
        invalidateAgentBuilderDashboardConsumers(queryClient, clientId, generatedDashboard.platformTypeId)
        rootDashboardId = getRootDashboardId(generatedDashboard)
      }

      if (!rootDashboardId) {
        throw new Error('Generated dashboard did not return a rootDashboardId.')
      }

      closeModal(true)
      navigate(`/reports/dashboard-builder/view?rootDashboardId=${encodeURIComponent(rootDashboardId)}`)
    } catch (error) {
      showModalError(error, 'Failed to create dashboard.')
    } finally {
      setIsPromptPolling(false)
    }
  }

  async function handleCreateFromJson() {
    setModalError('')
    if (!clientId) {
      setModalError('No active client selected.')
      return
    }
    if (!uploadedJson) {
      setModalError('Upload a dashboard JSON file first.')
      return
    }
    if (!platformTypeId) {
      setModalError('Select a platform before uploading a dashboard.')
      return
    }
    if (uploadedPlatformTypeId && uploadedPlatformTypeId !== platformTypeId) {
      setModalError('The selected platform must match the platformTypeId in the uploaded dashboard JSON.')
      return
    }
    const normalizedSection = isSecuredAgentBuilderPlatform(platformTypeId) ? normalizeDashboardSection(section, platformTypeId) : null
    if (isSecuredAgentBuilderPlatform(platformTypeId) && !normalizedSection) {
      setModalError('Select a dashboard section first.')
      return
    }

    try {
      const dashboard = await createFromJson.mutateAsync({
        clientId,
        platformTypeId,
        section: normalizedSection,
        title: uploadedTitle || null,
        dashboardJson: uploadedJson,
        isDraft: isDiAdmin && saveInitialAsDraft,
      })
      await redirectToCreatedDashboard(dashboard)
    } catch (error) {
      showModalError(error, 'Failed to upload dashboard.')
    }
  }

  const isCreating = createFromPrompt.isPending || createFromJson.isPending || isPromptPolling

  const uploadProps: UploadProps = {
    accept: '.json,application/json',
    maxCount: 1,
    showUploadList: false,
    disabled: isCreating,
    beforeUpload: (file) => {
      if (isCreating) return false
      void (async () => {
        try {
          const text = await file.text()
          const parsed = JSON.parse(text) as Partial<DashboardDefinition>
          const uploadedPlatformId = Number(parsed.platformTypeId)
          if (parsed.platformTypeId !== undefined && parsed.platformTypeId !== null && !Number.isFinite(uploadedPlatformId)) {
            throw new Error('platformTypeId must be a number')
          }
          if (Number.isFinite(uploadedPlatformId)) {
            if (platformOptions.length > 0 && !platformOptions.some((option) => option.value === uploadedPlatformId)) {
              throw new Error('The dashboard JSON uses a platform that is not available for the active client.')
            }
            setUploadedPlatformTypeId(uploadedPlatformId)
            setPlatformTypeId(uploadedPlatformId)
          } else {
            setUploadedPlatformTypeId(null)
          }
          setUploadedJson(text)
          setUploadedFileName(file.name)
          setUploadedTitle(extractDashboardTitle(text))
          const sectionPlatformTypeId = Number.isFinite(uploadedPlatformId) ? uploadedPlatformId : platformTypeId
          setSection(sanitizeDashboardSection(parsed.section, sectionPlatformTypeId))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'The selected file is not valid dashboard JSON.'
          addToast(message, 'error')
        }
      })()
      return false
    },
  }

  const renderDashboardCard = (dashboard: AgentBuilderDashboard) => {
    const sectionConfig = getSectionConfig(dashboard.section)
    const title = dashboard.title || 'Untitled Dashboard'

    return (
      <div
        key={`${getRootDashboardId(dashboard) ?? dashboard.id}-${sectionConfig.key}`}
        role="button"
        tabIndex={0}
        className="group flex h-[60px] w-full items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left shadow-card transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-brand-300"
        onClick={() => openDashboard(dashboard)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openDashboard(dashboard)
          }
        }}
      >
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm ${sectionConfig.iconClass}`}>
          {sectionConfig.icon}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className="block overflow-hidden text-xs font-semibold leading-4 text-slate-950"
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            }}
          >
            {title}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {canEditDashboard ? (
            <Tooltip title="Edit dashboard">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                aria-label={`Edit ${title}`}
                onClick={(event) => {
                  event.stopPropagation()
                  setEditingDashboard(dashboard)
                }}
              />
            </Tooltip>
          ) : null}
          {canDeleteAgentBuilderDashboard(session, dashboard, featureFlags) ? (
            <Tooltip title="Delete dashboard">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Delete ${title}`}
                loading={deleteDashboard.isPending}
                onClick={(event) => {
                  event.stopPropagation()
                  confirmDeleteDashboard(dashboard)
                }}
              />
            </Tooltip>
          ) : null}
          <Tooltip title={dashboard.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
            <Button
              type="text"
              size="small"
              className={dashboard.isFavorite ? '!text-brand-500' : '!text-gray-300 hover:!text-brand-500'}
              icon={dashboard.isFavorite ? <StarFilled /> : <StarOutlined />}
              aria-label={dashboard.isFavorite ? `Remove ${title} from favorites` : `Add ${title} to favorites`}
              loading={updateFavorite.isPending}
              onClick={(event) => {
                event.stopPropagation()
                toggleFavorite(dashboard)
              }}
            />
          </Tooltip>
        </span>
      </div>
    )
  }

  const renderDashboardSection = (
    config: DashboardSectionConfig,
    dashboards: AgentBuilderDashboard[],
    emptyText?: string,
  ) => (
    <section key={config.key} className="mb-6 last:mb-0">
      <div className="mb-3 flex items-center gap-2">
        <span className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wide ${config.accentClass}`}>
          <span className="text-sm">{config.icon}</span>
          {config.label}
        </span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>
      {dashboards.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-3">
          {dashboards.map(renderDashboardCard)}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 bg-white/70 px-4 py-5 text-sm text-gray-500">
          {emptyText ?? 'No dashboards in this section.'}
        </div>
      )}
    </section>
  )

  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd className="px-6 py-6 md:px-8">
      <div className="relative mx-auto w-full max-w-6xl">
        <div className="pt-1 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Typography.Title level={3} className="!mb-1">Agent Builder</Typography.Title>
              <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
            </div>
            <Space wrap>
              <Button icon={<ProfileOutlined />} disabled={!clientId} onClick={() => navigate('/reports/playbooks')}>
                Playbooks
              </Button>
              {canManageMcp ? (
                <Button icon={<SolutionOutlined />} disabled={!clientId} onClick={() => navigate('/reports/firm-profile')}>
                  Firm profile
                </Button>
              ) : null}
              {canManageMcp ? (
                <Button icon={<ApiOutlined />} disabled={!clientId} onClick={() => navigate('/reports/mcp-access')}>
                  MCP access
                </Button>
              ) : null}
              {canManageReportOptions ? (
                <Button icon={<SettingOutlined />} onClick={() => navigate('/reports/options')}>
                  Report options
                </Button>
              ) : null}
              {canCreateDashboards ? (
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  disabled={!clientId}
                  onClick={() => {
                    resetModal()
                    setModalOpen(true)
                  }}
                >
                  Create New Dashboard
                </Button>
              ) : null}
            </Space>
          </div>
        </div>

        <div className="py-3">
          <Space wrap size={12} className="w-full">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search dashboard titles"
              suffix={<SearchOutlined />}
              allowClear
              style={{ width: 260 }}
            />
            {isDiAdmin ? (
              <Select
                showSearch
                allowClear
                value={settingsQuery.data?.impersonationEmail || undefined}
                options={impersonationEmailOptions}
                optionFilterProp="label"
                optionLabelProp="value"
                onChange={(value) => void updateImpersonationEmail(value ?? null)}
                placeholder="Impersonate User"
                disabled={!clientId || settingsQuery.isLoading || updateSettings.isPending}
                loading={settingsQuery.isFetching || emailOptionsQuery.isFetching || updateSettings.isPending}
                notFoundContent={emailOptionsQuery.isFetching ? 'Loading...' : 'No active users found'}
                style={{ width: 280 }}
              />
            ) : null}
            <Tag color="cyan-inverse">{filteredRows.length} dashboards</Tag>
            {(dashboardsQuery.isLoading || dashboardsQuery.isFetching) && (
              <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
                <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                Loading...
              </span>
            )}
          </Space>
        </div>

        <div className="pt-2">
          {!clientId ? <Alert type="warning" showIcon className="mb-4" message="No active client selected." /> : null}
          {dashboardsQuery.isError ? (
            <Alert
              type="error"
              showIcon
              className="mb-4"
              message="Failed to load Agent Builder dashboards"
              description={dashboardsQuery.error instanceof Error ? dashboardsQuery.error.message : 'Unknown error'}
            />
          ) : null}

          {dashboardsQuery.isLoading ? (
            <div className="flex justify-center py-12">
              <Spin />
            </div>
          ) : filteredRows.length === 0 ? (
            <Empty description="No Agent Builder dashboards found." />
          ) : (
            <div>
              {renderDashboardSection(FAVORITES_SECTION, favoriteRows, 'No favorite dashboards yet.')}
              {groupedSections.map((item) => renderDashboardSection(item.config, item.dashboards))}
            </div>
          )}
        </div>
      </div>

      <Modal
        title="Create New Dashboard"
        open={modalOpen}
        onCancel={() => closeModal()}
        closable={!isCreating}
        maskClosable={!isCreating}
        keyboard={!isCreating}
        footer={[
          <Button key="cancel" onClick={() => closeModal()} disabled={isCreating}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={isCreating}
            disabled={isCreating || !platformTypeId || platformTypesQuery.isLoading}
            onClick={() => {
              if (activeTab === 'prompt') void handleCreateFromPrompt()
              else void handleCreateFromJson()
            }}
          >
            Create Dashboard
          </Button>,
        ]}
      >
        <Spin spinning={isCreating}>
          <Space direction="vertical" size={16} className="w-full">
            {modalError ? <Alert type="error" showIcon message={modalError} /> : null}
            {platformTypesQuery.isError ? (
              <Alert type="error" showIcon message="Client platform types could not be loaded. Please retry before creating a dashboard." />
            ) : null}
            {!platformTypesQuery.isLoading && !platformTypesQuery.isError && platformOptions.length === 0 ? (
              <Alert type="warning" showIcon message="No supported report platform is available for this client." />
            ) : null}
            <label className="flex flex-col gap-1">
              <Typography.Text type="secondary">Data platform</Typography.Text>
              <Select
                value={platformTypeId ?? undefined}
                options={platformOptions}
                onChange={handlePlatformTypeChange}
                loading={platformTypesQuery.isLoading}
                placeholder="Select a client platform"
                disabled={isCreating || platformTypesQuery.isLoading || platformOptions.length === 0}
              />
            </label>
            {isSecuredAgentBuilderPlatform(platformTypeId) ? (
              <label className="flex flex-col gap-1">
                <Typography.Text type="secondary">Dashboard Section</Typography.Text>
                <Select
                  value={section ?? undefined}
                  options={getDashboardSectionOptions(platformTypeId)}
                  onChange={setSection}
                  placeholder="Select section"
                  disabled={isCreating}
                />
              </label>
            ) : null}
            {isDiAdmin ? (
              <Checkbox
                checked={saveInitialAsDraft}
                onChange={(event) => setSaveInitialAsDraft(event.target.checked)}
                disabled={isCreating}
              >
                Save as draft
              </Checkbox>
            ) : null}
            <Typography.Text type="secondary">
              The selected platform determines the catalog and DAX fields available to the dashboard.
            </Typography.Text>
            <Tabs
              activeKey={activeTab}
              onChange={(nextTab) => {
                if (!isCreating) setActiveTab(nextTab)
              }}
              items={[
                {
                  key: 'prompt',
                  label: 'Prompt',
                  children: (
                    <Input.TextArea
                      rows={6}
                      value={prompt}
                      onChange={(event) => setPrompt(event.target.value)}
                      placeholder="Build staff utilisation by team with productivity and billable hours"
                      disabled={isCreating}
                    />
                  ),
                },
                {
                  key: 'upload',
                  label: 'Upload JSON',
                  children: (
                    <Space direction="vertical" size={12} className="w-full">
                      <Upload {...uploadProps}>
                        <Button icon={<UploadOutlined />} disabled={isCreating || !platformTypeId}>Upload Dashboard JSON</Button>
                      </Upload>
                      {uploadedFileName ? (
                        <Typography.Text type="secondary">
                          {uploadedFileName}{uploadedTitle ? ` - ${uploadedTitle}` : ''}
                        </Typography.Text>
                      ) : null}
                    </Space>
                  ),
                },
              ]}
            />
          </Space>
        </Spin>
      </Modal>

      <EditAgentBuilderDashboardModal
        open={!!editingDashboard}
        dashboard={editingDashboard}
        platformOptions={platformOptions}
        platformsLoading={platformTypesQuery.isLoading}
        saving={updateDashboard.isPending}
        onClose={() => setEditingDashboard(null)}
        onSave={async (values) => {
          const rootDashboardId = editingDashboard ? getRootDashboardId(editingDashboard) : null
          if (!rootDashboardId) {
            throw new Error('This dashboard is missing a rootDashboardId.')
          }

          await updateDashboard.mutateAsync({
            clientId,
            dashboardId: rootDashboardId,
            platformTypeId: values.platformTypeId,
            section: values.section,
          })
          addToast('Dashboard updated', 'success')
          setEditingDashboard(null)
        }}
      />
    </FeatureThemeProvider>
  )
}
